import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  Typography,
  Tabs,
  Tab,
  Avatar,
  Stack,
  Button,
} from "@mui/material";
import VenueMediaList from "./VenueTab/venueMedia";
import { Cancel, CheckCircle, LocationOn } from "@mui/icons-material";
import VenueReviews from "./VenueTab/VenueReviews";
import VenueMediaUser from "./VenueTab/venueMediaUser";
import VenuePreferences from "./VenueTab/VenuePreferences";
import ClaimedMedia from "./VenueTab/ReportClaimedMedia";
import CheckInTab from "./VenueTab/CheckinsTab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OperationalHours from "./VenueTab/OperationalHours";
import MainQuickSurveyTab from "./VenueTab/MainQuickSurveyTab";
import VenueEvent from "./VenueTab/VenueEvent";
import MainStarRatingTab from "./VenueTab/MainStarRatingTab";
import Interested from "./VenueTab/Interested";
import AssignVenueOwner from "./VenueTab/AssignVenueOwner";
import { useSelector } from "react-redux";

type coverImageType = {
  url: string;
  thumbnailUrl?: string; // optional if you also store thumbnails
};

type Venue = {
  _id: string;
  venueName: string;
  coverImage: coverImageType;
  venueImages: string[];
  cityOrCounty: string;
  stateOrRegion: string;
  country: string;
  phoneNumber: string;
  phoneCountryCode: string;
  venueType: string;
  isActive: boolean;
  isDelete: boolean;
  timeZone: any;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ProfileData {
  userId?: string;
  role?: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`venue-tabpanel-${index}`}
      aria-labelledby={`venue-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const VenueDetails: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const location = useLocation();
  const venue: Venue | undefined = location.state?.venue;
  const [tabValue, setTabValue] = useState(0);
  const isProfileData = useSelector((state: any) => state?.global?.profileData) as ProfileData | null;

  if (!venue)
    return (
      <Typography align="center" sx={{ mt: 10 }}>
        Venue not found
      </Typography>
    );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fullLocation = `${venue.cityOrCounty}, ${venue.stateOrRegion}, ${venue.country}`;

  return (
    <Box maxWidth="100%" mx="auto" p={0}>
      <Grid container spacing={3} style={{ borderRadius: "15px" }}>
        <Grid size={12}>
          <Card
            variant="outlined"
            sx={{ p: 3, position: "relative", borderRadius: "15px" }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid
                size={12}
                style={{ display: "flex", alignItems: "center", gap: "30px" }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <div style={{ position: "relative" }}>
                    <Avatar
                      src={venue.coverImage.url}
                      variant="rounded"
                      sx={{ width: 80, height: 80, borderRadius: "100px" }}
                    />
                    <div style={{ position: "absolute", top: 0, right: 0 }}>
                      {venue.isActive ? (
                        <CheckCircle fontSize="small" color="success" />
                      ) : (
                        <Cancel fontSize="small" sx={{ color: "grey.400" }} />
                      )}
                    </div>
                  </div>
                  <Box>
                    <Typography fontWeight="bold">{venue.venueName}</Typography>
                    <Typography color="text.secondary">
                      {venue.phoneCountryCode} {venue.phoneNumber}
                    </Typography>
                  </Box>
                </Stack>
                <div>
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{fullLocation}</Typography>
                    </Box>
                  </Stack>
                </div>
              </Grid>

              <Grid size={12} container spacing={2}>
                <Grid size={12}>
                  <VenueMediaList
                    venueId={venue._id}
                    venueName={venue.venueName}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Button
              style={{
                position: "absolute",
                right: "20px",
                top: "20px",
                borderColor: "black",
                color: "black",
              }}
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              <ArrowBackIcon style={{ rotate: "180deg" }} />
            </Button>
          </Card>
        </Grid>
      </Grid>

      <Card
        style={{ marginTop: "20px", borderRadius: "15px" }}
        variant="outlined"
      >
        {/* Tabs */}
        <Box>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Venue Tabs"
            sx={{
              "& .MuiTab-root": {
                color: "black",
                fontWeight: "bold",
                textTransform: "none",
              },
              "& .Mui-selected": {
                color: "white",
                fontWeight: "bold",
                backgroundColor: "black",
                padding: "15px",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "black",
              },

              borderBottom: "3px solid black",
            }}
          >

            <Tab label="Event" />
            <Tab label="User Media" />
            <Tab label="Check-Ins" />
            <Tab label="Reviews" />
            <Tab label="Star Rating" />
            <Tab label="Preferences" />
            <Tab label="Reported Media" />
            <Tab label="Operational Hours" />
            <Tab label="Quick Survey" />
            <Tab label="Interested" />
           {isProfileData?.role == 2 && <Tab label="Assign Owner" />}
          </Tabs>


          <TabPanel value={tabValue} index={0}>
            <VenueEvent venueId={venue._id} venueName={venue.venueName} timeZone={venue.timeZone} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <VenueMediaUser venueId={venue._id} venueName={venue.venueName} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <CheckInTab venueId={venue._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <VenueReviews venueId={venue._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <MainStarRatingTab venueId={venue._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <VenuePreferences venueId={venue._id} />
          </TabPanel>
          <TabPanel value={tabValue} index={6}>
            <ClaimedMedia venueId={venue._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={7}>
            <OperationalHours venueId={venue._id} />
          </TabPanel>
          <TabPanel value={tabValue} index={8}>
            <MainQuickSurveyTab venueId={venue._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={9}>
            <Interested venueId={venue._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={10}>
            <AssignVenueOwner venueId={venue._id} venueName={venue.venueName} />
          </TabPanel>
        </Box>
      </Card>
    </Box>
  );
};

export default VenueDetails;
